import React, {useEffect, useRef, useState} from "react";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import cn from "classnames";
import Link from "components/link";
import IconButton from "components/icon-button";
import {FormattedMessage} from "react-intl";
import Icon from "components/icon";
import {TableIconCell, CustomTableRow} from "components/table";
import ApiConnectedPaginatedTable from "../../../components/table/paginated-table/api-connected-paginated-table";
import {useTablePaginationContext} from "../../../components/table/paginated-table/table-pagination-context";
import {getRecordingsService} from "../../../services/recordingServices";
import {getEventService} from "../../../services/commonService";
import {mapRecordingFromRest} from "./rest-recording-adapter";
import OptionalDate from "components/optional-date";
import PropTypes from "prop-types";
import {HeaderCell} from "components/table";
import {DownloadRecordingFile} from "../utils";
import {injectIntl} from "react-intl";
import withStyles from "components/with-styles";
import styles from "./styles.css.js";
import {eventStatusTypes} from "pages/event";
import Tooltip from '@material-ui/core/Tooltip';
import {useAuthDataContext} from "components/auth";
import {differenceInDays} from "date-fns";
import {
    DOWNLOAD_STATUS_AVAILABLE,
    RECORDING_EXPIRATION_WARNING_DAYS,
    RECORDING_EXPIRATION_WARNING_DAYS_MAX
} from "../../../utils/constants";
import DownloadNotAvailable from "../../../components/buttons/download-button/parts/download-not-available";

const Header = ({classes}) => (
    <TableRow>
        <HeaderCell colSpan={3}>
      <span className={classes.mainHeader}>
        <FormattedMessage id="app.page.event.list"/>
      </span>
        </HeaderCell>
    </TableRow>
);

const StyledHeaderComponent = withStyles(styles)(Header);

const RowComponent = ({item, classes, intl, previousEvent, eventStatusMap}) => {
    const {hasAdminPrivileges, isGlobalAdmin} = useAuthDataContext();
    const detailsUrl = `/recording/details/${item.id}`;
    const itemEventStatus = eventStatusMap?.[item.eventId];
    const linkDisabled = itemEventStatus === eventStatusTypes.STARTED;
    const recordingAvailable = item?.availableFiles[0]?.downloadAvailable && item?.availableFiles[0]?.downloadStatus === DOWNLOAD_STATUS_AVAILABLE;

    const hasDifferentEvent = item.eventId !== previousEvent.current;
    const haveURL = item.availableFiles[0]?.path === "pending";

    const formattedDays = item?.expirationDate && differenceInDays(item?.expirationDate * 1000, new Date());

    const [warningText, setWarningText] = useState(null);

    if (hasDifferentEvent) {
        previousEvent.current = item.eventId;
    }

    const expirationWarningText = intl.formatMessage(
        {id: "recording.expiration.warning"},
        {
            days: formattedDays
        },
    );

    const handleSetWarningText = value => {
        setWarningText(value)
    };

    const handlerCopyToClipboard = (data, event) => {
        event.stopPropagation();
        navigator.clipboard.writeText(data.availableFiles[0].path);
    }

    return (item?.eventVisible || (hasAdminPrivileges || isGlobalAdmin) ?
            <>
                {hasDifferentEvent && (
                    <>
                        <TableRow className={classes.titleRow}>
                            <TableCell colSpan={4}>{item.event.title}</TableCell>
                        </TableRow>
                        <TableRow className={classes.subtitleRow}>
                            <TableCell colSpan={4}>
                                <FormattedMessage id="app.page.recording"/>
                            </TableCell>
                        </TableRow>
                    </>
                )}
                <CustomTableRow
                    key={item.id}
                    href={detailsUrl}
                    disabled={linkDisabled}
                    className={cn(classes.row, {
                        [classes.main]: item.isMainEventRecording,
                    })}
                >
                    <TableCell>
                        <div className={classes.title}>{item.event.title}</div>
                        <div>{item.description}</div>

                        <div>
                            <OptionalDate className={classes.date} value={item.created * 1000}/>
                        </div>
                    </TableCell>
                    <TableCell>
                        {item.event && (
                            <>
                                <div>
                                    <OptionalDate
                                        label={intl.formatMessage({id: "event.startDate"})}
                                        className={classes.date}
                                        icon="event-available"
                                        value={item.event.startDate * 1000}
                                    />
                                </div>
                                <div>
                                    <OptionalDate
                                        label={intl.formatMessage({id: "event.endDate"})}
                                        className={classes.date}
                                        icon="event-available"
                                        value={item.event.endDate * 1000}
                                    />
                                </div>
                            </>
                        )}
                    </TableCell>
                    <TableCell>
                        {item?.expirationDate &&
                        <div className={classes.cellContainer}>
                            <div>
                                <OptionalDate
                                    withTime={false}
                                    label={intl.formatMessage({id: "recording.expiration.date"})}
                                    className={classes.date}
                                    value={item?.expirationDate * 1000}
                                />
                            </div>
                            {formattedDays <= RECORDING_EXPIRATION_WARNING_DAYS && <div className={classes.container}>
                                <Icon color={"secondary"} name="ErrorOutlineRoundedIcon"/>
                                <div
                                    className={classes.warningText}>{formattedDays <= RECORDING_EXPIRATION_WARNING_DAYS_MAX ? intl.formatMessage({id: 'recording.expiration.warning.delete'}) : expirationWarningText}</div>
                            </div>}
                        </div>
                        }
                    </TableCell>
                    <TableIconCell>
                        {!linkDisabled && (
                            <>
                                {!haveURL &&
                                <Tooltip
                                    title={haveURL ? intl.formatMessage({id: "recording.copyurl.notavailable"}) : intl.formatMessage({id: "recording.copyurl"})}
                                    arrow>
                                    <span>
                                        <IconButton onClick={e => handlerCopyToClipboard(item, e)}>
                                            <Icon name="fileCopy"/>
                                        </IconButton>
                                    </span>
                                </Tooltip>}
                                <Tooltip title={intl.formatMessage({id: "recording.open.newtab"})} arrow>
                                    <span>
                                        <Link href={detailsUrl} target="_blank" rel="noopener" disabled={linkDisabled}>
                                            <IconButton>
                                                <Icon name="launch"/>
                                            </IconButton>
                                        </Link>
                                    </span>
                                </Tooltip>
                                <Tooltip title={intl.formatMessage({id: "common.download"})} arrow>
                                    <DownloadRecordingFile mainRecording={item.isMainEventRecording} onlyIcon
                                                           recording={item} disabled={!recordingAvailable}
                                                           handleSetWarningText={handleSetWarningText}/>
                                </Tooltip>
                            </>)}
                        {warningText === item.id && <DownloadNotAvailable showIcon={false}/>}
                    </TableIconCell>
                </CustomTableRow>
            </> : <></>
    );
};

RowComponent.propTypes = {
    /**
     ** Elemento a representar en la fila de la tabla
     **/
    item: PropTypes.object,
    /**
     ** Referencia con el último evento de la anterior grabación de la página
     **/
    previousEvent: PropTypes.object,
    /**
     ** Mapa eventId -> eventStatus de los eventos de las grabaciones de la página actual
     **/
    eventStatusMap: PropTypes.object,
};

const StyledI18nRowComponent = withStyles(styles)(injectIntl(RowComponent));

const RecordingPaginatedTable = ({page, variables, ...props}) => {
    const previousEvent = useRef();
    const {tablePaginationSize} = useTablePaginationContext();
    const perPage = tablePaginationSize;

    const [elements, setElements] = useState(null);
    const [loading, setLoading] = useState(null);
    const [error, setError] = useState(null);
    const [total, setTotal] = useState(null);
    const [eventStatusMap, setEventStatusMap] = useState({});

    const callback = response => {
        setLoading(false);
        setElements(response.data.map(mapRecordingFromRest));
        setTotal(parseInt(response.headers["pagination-total-results"]));
    };

    const errorCallback = value => {
        setLoading(false);
        setError(value);
    };

    useEffect(
        () => {
            setLoading(true);
            const params = {page, perPage, limit: perPage, ...variables};
            getRecordingsService(params, callback, errorCallback);
        },
        [page, perPage, variables],
    );

    useEffect(
        () => {
            if (!elements?.length) {
                setEventStatusMap({});
                return;
            }
            const uniqueEventIds = [...new Set(elements.map(item => item.eventId))];
            uniqueEventIds.forEach(eventId => {
                getEventService(
                    eventId,
                    eventData => setEventStatusMap(previous => ({...previous, [eventId]: eventData.event_status})),
                    () => {},
                );
            });
        },
        [elements],
    );

    return <ApiConnectedPaginatedTable
        APIPetition={getRecordingsService}
        headerComponent={StyledHeaderComponent}
        rowComponent={StyledI18nRowComponent}
        paginatedElementsHandler={data => data}
        page={page}
        elements={elements}
        loading={loading}
        error={error}
        total={total}
        perPage={perPage}
        rowProps={{
            previousEvent,
            eventStatusMap,
        }}
        {...props}
    />

};

RecordingPaginatedTable.propTypes = {
    /**
     ** Página a cargar en la tabla
     **/
    page: PropTypes.number,
    /**
     ** Objeto que contiene los filtrados de la búsqueda
     **/
    variables: PropTypes.object,
    /**
     ** Componente que se mostrará en el pie de la tabla
     **/
    footer: PropTypes.element,
};

export default withStyles(styles)(RecordingPaginatedTable);
